import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/DateTimePicker";
import { TravelTypePicker } from "@/components/requests/TravelTypePicker";
import { TripLocationFields } from "@/components/requests/TripLocationFields";
import { formatDate, toIso, toLocalDatetimeInput } from "@/lib/datetime";
import { EMPTY_TRIP_LOCATION, TripLocationValue, validateTripLocation } from "@/lib/trip";
import {
  ApiError,
  fetchAdvance,
  fetchReviewAdvanceAllowedCountries,
  fetchReviewAdvancePreviewLimit
} from "../../api";
import {
  AdvanceAmountsField,
  advanceAmountsError
} from "@/components/requests/AdvanceAmountsField";
import {
  OtherAdvanceAmountsField,
  otherAdvanceAmountsError
} from "@/components/requests/OtherAdvanceAmountsField";
import {
  Advance,
  AdvanceAmount,
  AllowedCountry,
  CLAIM_METHOD_LABEL,
  OTHER_CLAIM_TYPE_LABEL,
  TravelType
} from "../../types";

export interface ReviewAdvanceFields {
  /** One line per currency - see Advance.amounts. */
  amounts: AdvanceAmount[];
  reason: string;
  country: string;
  travelType: TravelType;
  originCountry: string | null;
  startCity: string | null;
  endCity: string | null;
  departureAt: string;
  arrivalAt: string;
  flightDetails: string;
}

/** Editable version of AdvanceDetailDialog, for an approver's Review
 *  action - advance-side twin of ReviewRequestDialog. The amount itself is
 *  still a direct field the approver edits (not derived from a policy rate
 *  the way a travel request's estimatedAllowanceAmount is), but the CAP
 *  each currency is checked against does depend on the trip's dates/
 *  country/travel type, so this shows a live per-currency limit preview
 *  (fetchReviewAdvancePreviewLimit) as those fields change - purely a
 *  read-only preview, never authoritative; the actual enforcement is still
 *  the server's own _validate_advance_edit at save time. */
export function ReviewAdvanceDialog({
  advanceId,
  open,
  onOpenChange,
  onSave,
  saving
}: {
  advanceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (fields: ReviewAdvanceFields) => void;
  saving: boolean;
}) {
  const [detail, setDetail] = React.useState<Advance | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [amounts, setAmounts] = React.useState<AdvanceAmount[]>([]);
  const [reason, setReason] = React.useState("");
  const [travelType, setTravelType] = React.useState<TravelType | "">("");
  const [location, setLocation] = React.useState<TripLocationValue>(EMPTY_TRIP_LOCATION);
  const [departureAt, setDepartureAt] = React.useState("");
  const [arrivalAt, setArrivalAt] = React.useState("");
  const [flightDetails, setFlightDetails] = React.useState("");

  const [allowedCountries, setAllowedCountries] = React.useState<AllowedCountry[]>([]);
  const [countriesLoading, setCountriesLoading] = React.useState(false);

  // Live per-currency cap preview - see fetchReviewAdvancePreviewLimit's own
  // doc comment. Before this, every row always read
  // limitsByCurrency={null}, so the "Policy limit for this trip" line under
  // each amount never showed an actual number here - only the static hint
  // below, and the real cap was only ever found out after Save round-
  // tripped and rejected an over-cap amount. Not authoritative - the server
  // re-checks this exact same cap at save time (_validate_advance_edit), so
  // a stale preview can never let an over-cap amount through.
  const [limitsByCurrency, setLimitsByCurrency] = React.useState<Record<
    string,
    number
  > | null>(null);
  const [limitsLoading, setLimitsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !advanceId) {
      setDetail(null);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    fetchAdvance(advanceId)
      .then((d) => {
        setDetail(d);
        setAmounts(d.amounts);
        setReason(d.reason || "");
        setTravelType(d.travelType || "");
        setLocation({
          country: d.country,
          originCountry: d.originCountry || "",
          startCity: d.startCity || "",
          endCity: d.endCity || ""
        });
        setDepartureAt(toLocalDatetimeInput(d.departureAt));
        setArrivalAt(toLocalDatetimeInput(d.arrivalAt));
        setFlightDetails(d.flightDetails || "");
      })
      .catch((err) =>
        setLoadError(err instanceof ApiError ? err.message : "Could not load this advance.")
      )
      .finally(() => setLoading(false));
  }, [open, advanceId]);

  React.useEffect(() => {
    if (!travelType || !advanceId) {
      setAllowedCountries([]);
      return;
    }
    setCountriesLoading(true);
    fetchReviewAdvanceAllowedCountries(advanceId, travelType, detail?.claimMethod || undefined)
      .then(setAllowedCountries)
      .catch(() => setAllowedCountries([]))
      .finally(() => setCountriesLoading(false));
    // See ReviewRequestDialog's identical effect for why claimMethod is
    // read once (not a reactive dep) and not itself editable here, and why
    // this is request/advance-scoped rather than the generic
    // fetchAllowedCountries (which resolves designation from the signed-in
    // APPROVER, not the requester - see fetchReviewAdvanceAllowedCountries).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelType, advanceId]);

  // Debounced live cap preview (400ms, same as ReviewRequestDialog's own
  // estimate preview) - fires whenever the fields the cap calculation
  // depends on change. Never fires for a currently-invalid date pair, same
  // reasoning as ReviewRequestDialog's canPreview.
  const canPreviewLimits = !!(
    travelType &&
    location.country &&
    departureAt &&
    arrivalAt &&
    new Date(arrivalAt) > new Date(departureAt)
  );
  React.useEffect(() => {
    if (!canPreviewLimits || !advanceId) {
      setLimitsByCurrency(null);
      return;
    }
    const timer = setTimeout(() => {
      setLimitsLoading(true);
      fetchReviewAdvancePreviewLimit(advanceId, {
        country: location.country,
        travelType: travelType as TravelType,
        departureAt: toIso(departureAt),
        arrivalAt: toIso(arrivalAt)
      })
        .then((result) => setLimitsByCurrency(result.limitsByCurrency))
        .catch(() => setLimitsByCurrency(null))
        .finally(() => setLimitsLoading(false));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPreviewLimits, advanceId, location.country, travelType, departureAt, arrivalAt]);

  // The trip's POLICY currency (from the chosen country's rate) - only marks
  // which amount line is capped; each line carries its own currency. Derived
  // straight from the country, so there's no separate state to keep in sync.
  const policyCurrency =
    allowedCountries.find((c) => c.country === location.country)?.currency ||
    "";
  // Currencies an amount row may use - the REQUESTER's policy countries
  // (fetchReviewAdvanceAllowedCountries above is requester-scoped).
  const allowedCurrencies = React.useMemo(
    () => Array.from(new Set(allowedCountries.map((c) => c.currency))).sort(),
    [allowedCountries]
  );

  // Same validate-then-confirm split as ReviewRequestDialog's own
  // handleSaveClick/confirmSave - see that file's comment.
  const [confirmingSave, setConfirmingSave] = React.useState(false);

  // Validation failures are toasted (top-right, red - see
  // components/ui/sonner.tsx), same reasoning as ReviewRequestDialog's
  // identical switch: an inline message at the end of the scrollable form
  // used to sit below Reason, invisible unless the approver scrolled all
  // the way down after clicking Save & approve from higher up.
  // An Other reimbursement advance (City travel / Miscellaneous): only its
  // purpose (the advance's name) and uncapped amounts are editable - the
  // server applies nothing else for it (_validate_other_advance_edit).
  const isOther = detail?.requestKind === "other";

  const handleSaveClick = () => {
    if (isOther) {
      if (!flightDetails.trim()) {
        toast.error("Purpose can't be empty.");
        return;
      }
      const otherError = otherAdvanceAmountsError(amounts);
      if (otherError) {
        toast.error(otherError);
        return;
      }
      setConfirmingSave(true);
      return;
    }
    if (!travelType) {
      toast.error("Select Domestic or International.");
      return;
    }
    const locationError = validateTripLocation(travelType, location);
    if (locationError) {
      toast.error(locationError);
      return;
    }
    if (!departureAt || !arrivalAt) {
      toast.error("Departure and arrival are both required.");
      return;
    }
    if (new Date(arrivalAt) <= new Date(departureAt)) {
      toast.error("Arrival must be after departure.");
      return;
    }
    // An advance is raised for a trip that hasn't happened yet (the opposite
    // of ReviewRequestDialog's completed-trip rule for a Reimbursement
    // request) - mirrors create_advance's own rule, by calendar date so
    // today is still allowed. The authoritative check is still server-side
    // (_validate_advance_edit).
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const departureDay = new Date(departureAt);
    departureDay.setHours(0, 0, 0, 0);
    const arrivalDay = new Date(arrivalAt);
    arrivalDay.setHours(0, 0, 0, 0);
    if (departureDay < today || arrivalDay < today) {
      toast.error("Departure and arrival can't be in the past.");
      return;
    }
    // Checked against the live preview when it's resolved (so an obviously
    // over-cap amount is caught here instead of round-tripping to find
    // out), null while it's still loading or unresolved - the REQUESTER's
    // entitlement is re-checked server-side either way on save (see
    // requests_api.py's _validate_advance_edit), so a stale/unresolved
    // preview can never let an over-cap amount actually through.
    const amountsError = advanceAmountsError(
      amounts,
      limitsByCurrency,
      allowedCurrencies,
      policyCurrency
    );
    if (amountsError) {
      toast.error(amountsError);
      return;
    }
    setConfirmingSave(true);
  };

  const confirmSave = () => {
    setConfirmingSave(false);
    if (isOther && detail) {
      // Only amounts and flightDetails are applied; the rest goes back
      // unchanged to satisfy the shared shape.
      onSave({
        amounts,
        reason: reason.trim(),
        country: detail.country,
        travelType: detail.travelType as TravelType,
        originCountry: null,
        startCity: null,
        endCity: null,
        departureAt: detail.departureAt,
        arrivalAt: detail.arrivalAt,
        flightDetails: flightDetails.trim()
      });
      return;
    }
    onSave({
      amounts,
      reason: reason.trim(),
      country: location.country,
      travelType: travelType as TravelType,
      originCountry: travelType === "international" ? location.originCountry : null,
      startCity: travelType === "domestic" ? location.startCity.trim() : null,
      endCity: travelType === "domestic" ? location.endCity.trim() : null,
      departureAt: toIso(departureAt),
      arrivalAt: toIso(arrivalAt),
      flightDetails: flightDetails.trim()
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Review & approve</SheetTitle>
          <SheetDescription>
            Fix any of the fields below, then save - this approves the advance
            with your edits in the same action.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mx-6 mt-4 flex-1">
          <div className="space-y-4 px-6">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {loadError && <p className="text-sm text-destructive">{loadError}</p>}

            {!loading && detail && isOther && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="review-advance-designation">Designation</Label>
                    <Input
                      id="review-advance-designation"
                      value={detail.designationAtSubmission || "-"}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="review-advance-claim-type">Claim type</Label>
                    <Input
                      id="review-advance-claim-type"
                      value={detail.claimType ? OTHER_CLAIM_TYPE_LABEL[detail.claimType] : "-"}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="review-advance-date">Planned date</Label>
                    <Input
                      id="review-advance-date"
                      value={detail.advanceDate ? formatDate(detail.advanceDate) : "-"}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="review-advance-purpose">Purpose</Label>
                  <Textarea
                    id="review-advance-purpose"
                    value={flightDetails}
                    onChange={(e) => setFlightDetails(e.target.value)}
                    rows={2}
                  />
                </div>
                <OtherAdvanceAmountsField value={amounts} onChange={setAmounts} idPrefix="review-advance" />
              </>
            )}

            {!loading && detail && !isOther && (
              <>
                <AdvanceAmountsField
                  value={amounts}
                  onChange={setAmounts}
                  policyCurrency={policyCurrency}
                  limitsByCurrency={limitsByCurrency}
                  allowedCurrencies={allowedCurrencies}
                  limitHint={
                    <p className="text-xs text-muted-foreground">
                      {limitsLoading
                        ? "Checking the requester's policy limit…"
                        : "The requester's policy limit is checked when you save."}
                    </p>
                  }
                  idPrefix="review-advance"
                />

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="review-advance-designation">
                      Designation
                    </Label>
                    <Input
                      id="review-advance-designation"
                      value={detail.designationAtSubmission || "-"}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="review-advance-claim-method">
                      Claim method
                    </Label>
                    <Input
                      id="review-advance-claim-method"
                      value={
                        detail.claimMethod
                          ? CLAIM_METHOD_LABEL[detail.claimMethod]
                          : "-"
                      }
                      disabled
                      readOnly
                    />
                  </div>
                </div>

                <TravelTypePicker
                  value={travelType}
                  onChange={(t) => {
                    setTravelType(t);
                    setLocation(EMPTY_TRIP_LOCATION);
                  }}
                />

                {travelType && (
                  <TripLocationFields
                    travelType={travelType}
                    value={location}
                    onChange={setLocation}
                    allowedCountries={allowedCountries}
                    loading={countriesLoading}
                    emptyHint="No countries are configured for this travel type yet."
                    idPrefix="review-advance"
                    showCurrency={false}
                  />
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Departure</Label>
                    <DateTimePicker
                      value={departureAt}
                      onChange={setDepartureAt}
                      minDate={new Date()}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Arrival</Label>
                    <DateTimePicker
                      value={arrivalAt}
                      onChange={setArrivalAt}
                      minDate={new Date()}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="review-advance-flight">Purpose of travel</Label>
                  <Textarea
                    id="review-advance-flight"
                    value={flightDetails}
                    onChange={(e) => setFlightDetails(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="review-advance-reason">Any Other Request (Optional)</Label>
                  <Textarea
                    id="review-advance-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                  />
                </div>

              </>
            )}
          </div>
        </ScrollArea>

        <SheetFooter className="mt-4 gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} disabled={loading || saving || !detail}>
            {saving ? "Saving…" : "Save & approve"}
          </Button>
        </SheetFooter>
      </SheetContent>

      <ConfirmDialog
        open={confirmingSave}
        onOpenChange={setConfirmingSave}
        title="Save these edits and approve this advance?"
        description="Your changes will be applied and the advance moves forward as Approved in the same action - this can't be undone from here."
        confirmLabel="Save & approve"
        pending={saving}
        onConfirm={confirmSave}
      />
    </Sheet>
  );
}
